import lottieLight, {
  AnimationItem
} from 'lottie-web/build/player/lottie_light';
import React, {
  useRef,
  useEffect,
  useLayoutEffect,
  memo,
  useState
} from 'react';

type ViewType =
  | 'audio'
  | 'pause'
  | 'circle'
  | 'check'
  | 'success'
  | 'addMediaInFolder'
  | 'addCampaign'
  | 'addShareLink'
  | 'paperClip'
  | 'arrow'
  | 'preloader'
  | 'noSearchingResult'
  | 'signIn'
  | 'star'
  | 'upload'
  | 'click'
  | 'confetti';

// Loaded animations, so remounted components render them right away
const animationDataCache = new Map<ViewType, unknown>();

type LottieComponentProps = {
  view: ViewType;
  className?: string;
  isStopped?: boolean;
  autoPlay?: boolean;
  isLooped?: boolean;
  progress?: number;
};

export default memo(function LottieComponent({
  view,
  className = undefined,
  isStopped = false,
  autoPlay = true,
  isLooped = true,
  progress = 0
}: LottieComponentProps) {
  const lottie = useRef<AnimationItem>();
  const [icon, setIcon] = useState<unknown>(() => animationDataCache.get(view));

  const ref = useRef<HTMLDivElement>(null);
  const autoPlayRef = useRef(autoPlay);
  const isLoopedRef = useRef(isLooped);

  autoPlayRef.current = autoPlay;
  isLoopedRef.current = isLooped;

  useEffect(() => {
    const cached = animationDataCache.get(view);
    if (cached) {
      setIcon(cached);
      return;
    }
    (async () => {
      const { default: icon } = await import(
        `./utils/${view}.${view === 'confetti' ? 'json' : 'ts'}`
      );
      const animationData = await icon;
      animationDataCache.set(view, animationData);
      setIcon(animationData);
    })();
  }, [view]);

  useEffect(() => {
    if (!lottie.current) return;
    if (isStopped) {
      lottie.current.goToAndStop(0);
    } else lottie.current.play();
  }, [isStopped]);

  useEffect(() => {
    if (!lottie.current || !progress) return;
    const frames = lottie.current.getDuration(true) / 100;
    lottie.current.goToAndStop(frames * progress, true);
  }, [progress]);

  // Layout effect: the animation is in place before the first paint
  useLayoutEffect(() => {
    if (!icon || !ref.current) return;
    ref.current.innerHTML = '';
    const animation = lottieLight.loadAnimation({
      renderer: 'svg',
      loop: isLoopedRef.current,
      autoplay: autoPlayRef.current,
      animationData: icon,
      container: ref.current
    });
    lottie.current = animation;
    return () => {
      animation.destroy();
      if (lottie.current === animation) lottie.current = undefined;
    };
  }, [icon]);

  return <div ref={ref} className={className} />;
});
